'use client';

// import type { NavSectionProps } from 'src/components/nav-section';
import type { Theme, SxProps, Breakpoint } from '@mui/material/styles';

import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import { iconButtonClasses } from '@mui/material/IconButton';

// import { useBoolean } from 'src/hooks/use-boolean';

// import { allLangs } from 'src/locales';
// import { _contacts, _notifications } from 'src/_mock';

// import { Logo } from 'src/components/logo';
// import { useSettingsContext } from 'src/components/settings';

import { Main } from './main';
import { useBoolean } from '@/hooks/use-boolean';
import { useSettingsContext } from '@/components/settings';
import { LayoutSection } from '@/layouts/core/layout-section';
import { HeaderSection } from '@/layouts/core/header-section';
import { NavSectionProps } from '@/components/nav-section';
import { NavHorizontal } from './nav-horizontal';
import { NavMobile } from '@/layouts/dashboard/nav-mobile';
import { MenuButton } from '../components/menu-button';
import { StyledDivider, useNavColorVars } from '@/layouts/dashboard/styles';
import { WorkspacesPopover } from '@/layouts/components/workspaces-popover';
import { allLangs } from '@/locales';
import { LanguagePopover } from '@/layouts/components/language-popover';
import { Searchbar } from '@/layouts/components/searchbar';
import { NavVertical } from '@/layouts/dashboard/nav-vertical';
import { NotificationsDrawer } from '@/layouts/components/notifications-drawer';
import { SettingsButton } from '@/layouts/components/settings-button';
import { AccountDrawer } from '@/layouts/components/account-drawer';
import { layoutClasses } from '@/layouts/classes';
import { Logo } from '../../components/logo/logo'
import { LayoutComponent } from '@/components/new_ui/LayoutComponent';
import { SvgIcon } from '@mui/material';
import { Iconify } from '@/components/iconify';
import { GQLQuery } from '@/lib/client';
import { Q_CLIENTS_T1 } from '@/lib/queries/client-t1.query';
import { useState, useEffect } from 'react';
import Permission from '@/components/Permission';
import { useTranslatedNavData } from '@/components/nav-translator';
import LightDarkSwitch from '@/layouts/components/light-dark-switch';

export type DashboardLayoutProps = {
  sx?: SxProps<Theme>;
  children: React.ReactNode;
  header?: {
    sx?: SxProps<Theme>;
  };
  data?: {
    nav?: NavSectionProps['data'];
    user?: any;
  };
};

const dashboardNavData: NavSectionProps['data'] = []

// ----------------------------------------------------------------------

export function DashboardLayout({ sx, children, header, data }: DashboardLayoutProps) {
  const theme = useTheme();

  const mobileNavOpen = useBoolean();

  const settings = useSettingsContext();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const navColorVars = useNavColorVars(theme, settings);

  const layoutQuery: Breakpoint = 'lg';

  const rawNavData: any = data?.nav ?? dashboardNavData;
  const navData = useTranslatedNavData(rawNavData);
  const isNavMini = settings.navLayout === 'mini';
  const isNavHorizontal = settings.navLayout === 'horizontal';
  const isNavVertical = isNavMini || settings.navLayout === 'vertical';
  const _account: any[] = [
    {
      label: 'Home',
      href: "/" + navData[0]?.items[0]?.path,
      icon: <Iconify icon="solar:home-angle-bold-duotone" />,
    },
            {
              label: 'Profile',
              href: '/agent/profile',
              icon: (
                <SvgIcon>
                  <path
                    opacity="0.5"
                    d="M2.28099 19.6575C2.36966 20.5161 2.93261 21.1957 3.77688 21.3755C5.1095 21.6592 7.6216 22 12 22C16.3784 22 18.8905 21.6592 20.2232 21.3755C21.0674 21.1957 21.6303 20.5161 21.719 19.6575C21.8505 18.3844 22 16.0469 22 12C22 7.95305 21.8505 5.6156 21.719 4.34251C21.6303 3.48389 21.0674 2.80424 20.2231 2.62451C18.8905 2.34081 16.3784 2 12 2C7.6216 2 5.1095 2.34081 3.77688 2.62451C2.93261 2.80424 2.36966 3.48389 2.28099 4.34251C2.14952 5.6156 2 7.95305 2 12C2 16.0469 2.14952 18.3844 2.28099 19.6575Z"
                    fill="currentColor"
                  />
                  <path
                    d="M13.9382 13.8559C15.263 13.1583 16.1663 11.7679 16.1663 10.1666C16.1663 7.8655 14.3008 6 11.9996 6C9.69841 6 7.83291 7.8655 7.83291 10.1666C7.83291 11.768 8.73626 13.1584 10.0612 13.856C8.28691 14.532 6.93216 16.1092 6.51251 18.0529C6.45446 18.3219 6.60246 18.5981 6.87341 18.6471C7.84581 18.8231 9.45616 19 12.0006 19C14.545 19 16.1554 18.8231 17.1278 18.6471C17.3977 18.5983 17.5454 18.3231 17.4876 18.0551C17.0685 16.1103 15.7133 14.5321 13.9382 13.8559Z"
                    fill="currentColor"
                  />
                </SvgIcon>
              ),
            },
          ]

  if (!mounted) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Main isNavHorizontal={false}>{children}</Main>
      </Box>
    );
  }

  return (
    <LayoutSection
      /** **************************************
       * Header
       *************************************** */
      headerSection={
        <HeaderSection
          layoutQuery={layoutQuery}
          disableElevation={isNavVertical}
          slotProps={{
            toolbar: {
              sx: {
                ...(isNavHorizontal && {
                  bgcolor: 'var(--layout-nav-bg)',
                  [`& .${iconButtonClasses.root}`]: {
                    color: 'var(--layout-nav-text-secondary-color)',
                  },
                  [theme.breakpoints.up(layoutQuery)]: {
                    height: 'var(--layout-nav-horizontal-height)',
                  },
                }),
              },
            },
            container: {
              maxWidth: false,
              sx: {
                ...(isNavVertical && { px: { [layoutQuery]: 5 } }),
              },
            },
          }}
          sx={header?.sx}
          slots={{
            topArea: (
              <Alert severity="info" sx={{ display: 'none', borderRadius: 0 }}>
                This is an info Alert.
              </Alert>
            ),
            bottomArea: isNavHorizontal ? (
              <NavHorizontal
                data={navData}
                layoutQuery={layoutQuery}
                cssVars={navColorVars.section}
              />
            ) : null,
            leftArea: (
              <>
                {/* -- Nav mobile -- */}
                <MenuButton
                  onClick={mobileNavOpen.onTrue}
                  sx={{
                    mr: 1,
                    ml: -1,
                    [theme.breakpoints.up(layoutQuery)]: { display: 'none' },
                  }}
                />
                <NavMobile
                  data={navData}
                  open={mobileNavOpen.value}
                  onClose={mobileNavOpen.onFalse}
                  cssVars={navColorVars.section}
                />
                {/* -- Logo -- */}
                {isNavHorizontal && (
                  <Logo
                    sx={{
                      display: 'none',
                      [theme.breakpoints.up(layoutQuery)]: { display: 'inline-flex' },
                    }}
                  />
                )}
                {/* -- Divider -- */}
                {isNavHorizontal && (
                  <StyledDivider
                    sx={{ [theme.breakpoints.up(layoutQuery)]: { display: 'flex' } }}
                  />
                )}
                {/* -- Workspaces popover -- */}
                <Permission permission="clienttier1.read-active">
                <WorkspacesPopover
                  sx={{ color: 'var(--layout-nav-text-primary-color)' }}
                />
                </Permission>

              </>
            ),
            rightArea: (
              <Box display="flex" alignItems="center" gap={{ xs: 0, sm: 0.75 }}>
                {/* -- Searchbar -- */}
                <Searchbar data={navData} />
                {/* -- Language popover -- */}
                <LanguagePopover data={allLangs} />
                {/*light dark mode switch*/}
                <LightDarkSwitch />
                {/* -- Agent notifications -- */}
                <NotificationsDrawer enabled={Boolean(data?.user?.agent?.id)} />
                {/* -- Contacts popover -- */}
                {/*<ContactsPopover data={_contacts} />*/}
                {/* -- Settings button -- */}
                <SettingsButton />
                {/* -- Account drawer -- */}
                <AccountDrawer data={_account} session={data && data.user || null} />
              </Box>
            ),
          }}
        />
      }
      /** **************************************
       * Sidebar
       *************************************** */
      sidebarSection={
        isNavHorizontal ? null : (
          <NavVertical
            data={navData}
            isNavMini={isNavMini}
            layoutQuery={layoutQuery}
            cssVars={navColorVars.section}
            onToggleNav={() =>
              settings.onUpdateField(
                'navLayout',
                settings.navLayout === 'vertical' ? 'mini' : 'vertical'
              )
            }
          />
        )
      }
      /** **************************************
       * Footer
       *************************************** */
      footerSection={null}
      /** **************************************
       * Style
       *************************************** */
      cssVars={{
        ...navColorVars.layout,
        '--layout-transition-easing': 'linear',
        '--layout-transition-duration': '120ms',
        '--layout-nav-mini-width': '88px',
        '--layout-nav-vertical-width': '300px',
        '--layout-nav-horizontal-height': '64px',
        '--layout-dashboard-content-pt': theme.spacing(1),
        '--layout-dashboard-content-pb': theme.spacing(8),
        '--layout-dashboard-content-px': theme.spacing(5),
      }}
      sx={{
        [`& .${layoutClasses.hasSidebar}`]: {
          [theme.breakpoints.up(layoutQuery)]: {
            transition: theme.transitions.create(['padding-left'], {
              easing: 'var(--layout-transition-easing)',
              duration: 'var(--layout-transition-duration)',
            }),
            pl: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
          },
        },
        ...sx,
      }}
    >
      <Main isNavHorizontal={isNavHorizontal}>{children}</Main>
    </LayoutSection>
  );
}
